import React, { useEffect, useState } from 'react';
import {
    IonButton,
    IonCheckbox,
    IonCol,
    IonContent,
    IonFooter,
    IonGrid,
    IonHeader,
    IonInput,
    IonItem,
    IonLabel,
    IonList,
    IonListHeader,
    IonLoading,
    IonPage,
    IonRow,
    IonSelect,
    IonSelectOption,
    IonToolbar,
} from '@ionic/react';
import { addUpdate } from '../utilities/Updates';
import { getJob, getJobChecklists, updateStoredObjectKey } from '../utilities/Storage';
import moment from 'moment';
import { Toolbar } from './Toolbar';
import { storeChecklistForJob } from '../utilities/ApiTasks';
import { FlagChecklistPanel } from './FlagChecklistPanel';
import { ChecklistFlagInfo } from '../dataStructures/checklist/ChecklistFlagInfo';
import { userIsCleaner } from '../dataStructures/user/predicates';
import { useAuthentication } from '../authentication/authentication-hook';

function answerFieldByQuestionType(questionType) {
    if ('text' === questionType || 'multi' === questionType) {
        return 'answer';
    }

    if ('checklist' === questionType) {
        return 'answers';
    }

    if ('check' === questionType) {
        return 'checked';
    }

    throw new Error(`Invalid question type ${ questionType }`);
}

function unserialiseChecklistForInitialState(json) {
    const structure = {
        groups: [],
        questionsByGroupId: {},
        answersByQuestionId: {},
    };

    if (!json) {
        return structure;
    }

    let checklist = {};
    try {
        checklist = JSON.parse(json);
    } catch (e) {
        console.error('Failed to unserialise checklist JSON', e);
    }

    if ('groups' in checklist) {
        checklist.groups.forEach(group => {
            const groupId = group.id;

            if ('questions' in group) {
                structure.questionsByGroupId[groupId] = [];
                structure.groups = [
                    ...structure.groups,
                    group,
                ];

                group.questions.forEach(question => {
                    const questionId = question.id;
                    structure.questionsByGroupId[groupId].push(question);
                    structure.answersByQuestionId[questionId] = question[answerFieldByQuestionType(question.type)];
                });
            }
        });
    }

    return structure;
}

function serialiseChecklistFromState(state) {
    const response = {
        '$version': 2,
    };

    const serialiseQuestion = (question) => {
        const questionId = question.id;

        if (questionId in state.answersByQuestionId) {
            return {
                ...question,
                [answerFieldByQuestionType(question.type)]: state.answersByQuestionId[questionId],
            };
        }

        return null;
    };

    response.groups = state.groups.map(group => {
        const groupId = group.id;
        return {
            ...group,
            questions: state.questionsByGroupId[groupId].map(serialiseQuestion).filter(Boolean),
        };
    });

    return JSON.stringify(response);
}

function getResponseSnapshotOrChecklist(foundResponse) {
    const snapshot = foundResponse?.json ?? '{}';
    const checklist = foundResponse?.pmschecklist?.json ?? '{}';

    if ('{}' === snapshot) {
        return checklist;
    }

    if (snapshot.includes('"groups"')) {
        return snapshot;
    }

    return checklist;
}

export default function JobChecklists({
    history,
    match,
}) {
    const { user } = useAuthentication();
    const isCleaner = userIsCleaner(user);

    const jobId = Number(match.params.id);
    const jobChecklistId = Number(match.params.id2);

    const [ checklistId, setChecklistId ] = useState(null);

    const [ flagInfo, setFlagInfo ] = useState(new ChecklistFlagInfo(false, ''));
    const [ draftFlagReason, setDraftFlagReason ] = useState('');
    const [ isAlreadyFlagged, setIsAlreadyFlagged ] = useState(false);

    const [ groups, setGroups ] = useState([]);
    const [ questionsByGroupId, setQuestionsByGroupId ] = useState({});
    const [ answersByQuestionId, setAnswersByQuestionId ] = useState({});

    const [ isLoading, setIsLoading ] = useState(true);

    const showFlagPanel = !isLoading && isCleaner;

    useEffect(() => {
        const getChecklist = async () => {
            // In case the job's checklists or their response haven't been preloaded, we'll make a request to ensure
            // we have data to present.
            const preloadedJobChecklists = await getJobChecklists(jobId);
            if (preloadedJobChecklists.length < 1) {
                await storeChecklistForJob(jobId);
            }

            const jobChecklists = await getJobChecklists(jobId);
            const foundResponse = jobChecklists.find(({ id }) => jobChecklistId === id);

            if (undefined === foundResponse) {
                throw new Error(`Unexpected state: The checklist and response for Job ${ jobId } (WorkOrderPmsChecklist ${ jobChecklistId }) weren't preloaded, user must've reached this page directly and with a cold offline cache.`);
            }

            const structureToRender = unserialiseChecklistForInitialState(getResponseSnapshotOrChecklist(foundResponse));

            setChecklistId(jobChecklistId);

            setGroups(structureToRender.groups);
            setQuestionsByGroupId(structureToRender.questionsByGroupId);
            setAnswersByQuestionId(structureToRender.answersByQuestionId);

            const initialFlagged = foundResponse?.flaggedForReview ?? false;
            const initialReason = foundResponse?.flaggedReason ?? '';

            setFlagInfo(new ChecklistFlagInfo(initialFlagged, initialReason));
            setDraftFlagReason(initialReason);
            setIsAlreadyFlagged(initialFlagged);
            setIsLoading(false);
        };

        setIsLoading(true);

        getChecklist();
    }, [ jobId, jobChecklistId, setGroups, setQuestionsByGroupId, setAnswersByQuestionId, setChecklistId, setFlagInfo, setIsLoading ]);

    const handleChange = (questionId, value) => {
        setAnswersByQuestionId((answers) => ({
            ...answers,
            [questionId]: value,
        }));
    };

    const handleSave = async () => {
        setIsLoading(true);

        const updateddatetime = moment().format('YYYY-MM-DD HH:mm:ss');
        const serialisedResponse = serialiseChecklistFromState({
            groups,
            questionsByGroupId,
            answersByQuestionId,
        });

        const updates = {
            updateddatetime,
            json: serialisedResponse,
            flagReason: flagInfo?.reason,
            flagForReview: flagInfo?.flagged,
        };

        await addUpdate('update_checklist', {
            jobId,
            workOrderPmsChecklistId: checklistId,
            obj: updates,
        });

        // Update the job in cache with the changes queued for update.
        const jobChecklists = await getJobChecklists(jobId);
        const job = await getJob(jobId);
        await updateStoredObjectKey('jobs', jobId, {
            ...job,
            checklists: jobChecklists.map(checklist => {
                if (checklist.id === jobChecklistId) {
                    return {
                        ...checklist,
                        ...updates,
                        flaggedReason: flagInfo?.reason,
                        flaggedForReview: flagInfo?.flagged,
                    };
                }
                return checklist;
            }),
        });

        setIsLoading(false);
        history.goBack();
    };

    return (
        <IonPage>
            <IonHeader>
                <Toolbar title="Checklist" backButtonHref="/"/>
            </IonHeader>
            <IonContent>
                <IonLoading
                    isOpen={ isLoading }
                />
                { showFlagPanel &&
                    <FlagChecklistPanel
                        flagged={ flagInfo.flagged }
                        reason={ draftFlagReason }
                        isReadOnly={ isAlreadyFlagged }
                        onToggle={ newFlagged => {
                            setFlagInfo(info =>
                                new ChecklistFlagInfo(
                                    newFlagged,
                                    newFlagged ? draftFlagReason : '',
                                ),
                            );
                        } }
                        onReasonChange={ newReason => {
                            setDraftFlagReason(newReason);
                            setFlagInfo(
                                new ChecklistFlagInfo(true, newReason),
                            );
                        } }
                    /> }
                { groups.map((group, gi) =>
                    <IonList key={ group.id }>
                        <IonListHeader>
                            { group.name }
                        </IonListHeader>
                        { questionsByGroupId[group.id]?.map(question => {
                            if (question.type === 'multi') {
                                return (
                                    <DropdownField
                                        key={ question.id }
                                        question={ question }
                                        value={ answersByQuestionId[question.id] ?? undefined }
                                        handleChange={ value => handleChange(question.id, value) }
                                    />
                                );
                            }
                            if (question.type === 'text') {
                                return (
                                    <TextField
                                        key={ question.id }
                                        question={ question }
                                        value={ answersByQuestionId[question.id] ?? '' }
                                        handleChange={ value => handleChange(question.id, value) }
                                    />
                                );
                            }
                            if (question.type === 'checklist') {
                                return (
                                    <CheckboxField
                                        key={ question.id }
                                        question={ question }
                                        value={ answersByQuestionId[question.id] ?? [] }
                                        handleChange={ value => handleChange(question.id, value) }
                                    />
                                );
                            }
                            if (question.type === 'check') {
                                return (
                                    <ToggleField
                                        key={ question.id }
                                        question={ question }
                                        value={ answersByQuestionId[question.id] ?? undefined }
                                        handleChange={ value => handleChange(question.id, value) }
                                    />
                                );
                            }
                            return null;
                        }) }
                    </IonList>,
                ) }
            </IonContent>
            <IonFooter no-border>
                <IonToolbar className="ion-text-center">
                    <IonGrid>
                        <IonRow>
                            <IonCol>
                                <IonButton
                                    color="medium"
                                    expand="block"
                                    onClick={ handleSave }
                                >
                                    save
                                </IonButton>
                            </IonCol>
                        </IonRow>
                    </IonGrid>
                </IonToolbar>
            </IonFooter>
        </IonPage>
    );
}

function DropdownField({
    question,
    value,
    handleChange,
}) {
    return (
        <IonItem className={ 'checklist-question checklist-question--dropdown' }>
            <IonLabel className="ion-text-wrap">{ question.question }</IonLabel>
            <IonSelect
                placeholder="Select an option"
                value={ value }
                onIonChange={ e => handleChange(e.target.value) }
            >
                { question.options.map((option) =>
                    <IonSelectOption
                        key={ option.id }
                        value={ option.id }
                    >{ option.option }</IonSelectOption>,
                ) }
            </IonSelect>
        </IonItem>
    );
}

function TextField({
    question,
    value,
    handleChange,
}) {
    return (
        <IonItem className={ 'checklist-question checklist-question--text' }>
            <IonLabel
                className="ion-text-wrap"
                position="stacked"
            >{ question.question }</IonLabel>
            <IonInput
                value={ value }
                onIonChange={ e => handleChange(e.target.value) }
            />
        </IonItem>
    );
}

function CheckboxField({
    question,
    value,
    handleChange,
}) {
    const simpleRow = 2 === question.options.length;
    return (
        <IonItem className={ 'checklist-question checklist-question--checkbox-group' }>
            <IonGrid>
                <IonRow>
                    <IonCol
                        size={ '12' }
                        size-md={ simpleRow ? '6' : '12' }
                    >
                        <IonLabel className="ion-text-wrap">{ question.question }</IonLabel>
                    </IonCol>
                    { question.options.map((option) =>
                        <IonCol
                            size={ '12' }
                            size-md={ simpleRow ? '3' : '12' }
                            key={ option.id }
                        >
                            <IonItem>
                                <IonCheckbox
                                    slot="end"
                                    checked={ value.includes(option.id) }
                                    onIonChange={ e => {
                                        let v = [ ...value ];
                                        if (e.target.checked) {
                                            v.push(option.id);
                                        } else {
                                            v.splice(v.indexOf(option.id), 1);
                                        }

                                        handleChange(v);
                                    } }
                                />
                                <IonLabel className="ion-text-wrap">{ option.option }</IonLabel>
                            </IonItem>
                        </IonCol>,
                    ) }
                </IonRow>
            </IonGrid>
        </IonItem>
    );
}

function ToggleField({
    question,
    value,
    handleChange,
}) {
    return (
        <IonItem className={ 'checklist-question checklist-question--toggle' }>
            <IonLabel className="ion-text-wrap">{ question.question }</IonLabel>
            <IonCheckbox
                slot="end"
                checked={ value }
                onIonChange={ e => handleChange(e.target.checked) }
            />
        </IonItem>
    );
}
